import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import {
	AbstractControl,
	FormArray,
	FormControl,
	FormGroup,
	Validators,
	FormsModule,
	ReactiveFormsModule
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import moment from 'moment';
import { debounceTime, Subscription } from 'rxjs';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatDatepickerModule } from '@copo/mat-datepicker-new';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor, NgClass } from '@angular/common';

import { Batch } from '../../../../order/models/batch';
import { ShipToDetails } from '../../../../order/models/ship-to-details';
import { DATE_CRITERION_TYPE } from '../../../../shared/models/filter-formats';
import { WEIGHT_OPTIONS } from '../../../../shared/models/list';
import { GROSS } from '../../../../shared/models/settings';
import { User } from '../../../../shared/models/user';
import { UserService } from '../../../../shared/services/user.service';
import { CalledBatch, EcallOffPageContent, RDD_MODE } from '../../../models/ecall-off-model';
import { ECallOffService } from '../../../services/ecall-off.service';
import {
	EcallOffHeaderForm,
	EcallOffHeaderInit,
	EcallOffHeaderComponent
} from '../../header/ecall-off-header.component';
import {
	InfoEntryCalloffHeaderForm,
	InfoEntryCalloffHeaderComponent
} from '../../infoentry/infoentry-calloff-header.component';
import { DialogSaveCommentComponent } from '../dialog-save-comment/dialog-save-comment.component';
import { NumberPipe } from '../../../../shared/pipes/number.pipe';
import { CopoDatepickerInputDirective } from '../../../../shared/directives/date-picker.directive';
import { EcallOffShipToDetails } from '../../../../order/models/ecall-off-ship-to-details';
import { MillSelectorMill } from '../../mill/mill-selector.component';

interface SelectedBatches {
	id: number;
	comment: string;
	fromDate: string;
	toDate: string;
	requestedDate: string;
	unloadingReference: string;
	millCode: string;
	millDescription: string;
	customerOrderReference: string;
	customerArticleReference: string;
	shipTo?: ShipToDetails;
	supplierOrderReference: string;
	supplierOrderItemNumber: string;
}
@Component({
	selector: 'copo-step-infoentry',
	templateUrl: './step-infoentry.component.html',
	styleUrl: '../ecall-off-steps.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		EcallOffHeaderComponent,
		InfoEntryCalloffHeaderComponent,
		FormsModule,
		ReactiveFormsModule,
		NgIf,
		NgFor,
		NgClass,
		MatIconModule,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,

		MatButtonModule,

		MatSelectModule,

		MatOptionModule,
		CopoDatepickerInputDirective,
		MatDatepickerModule,

		MatMenuModule,
		MatDividerModule,
		NumberPipe
	]
})

export class StepInfoEntryComponent extends EcallOffPageContent implements OnDestroy {
	rddModes = RDD_MODE;
	formatType: string = DATE_CRITERION_TYPE;
	currentDate = moment().toDate();

	reqDate = false;
	fromTo = false;
	user?: User;
	infoEntryHeaderFormGroup?: FormGroup;
	callOffFormGroup: FormGroup;
	selectedBatches: Batch[] = [];
	tempSelectedBatches: Batch[] = [];
	calledBatches: CalledBatch[] = [];
	selectedMills: MillSelectorMill[] = [];
	dialogSubscription: Subscription = new Subscription();
	orderSub?: Subscription;
	weightDisplay: keyof typeof WEIGHT_OPTIONS = GROSS;
	callOffFormGroupValidity = false;
	custCallOffRef?: string;
	rddMode?: string;
	currentVisibleOrgId?: number;
	shipToDetails?: EcallOffShipToDetails[];
	allowShiptoSelection = false;
	copyTo?: string;
	sub?: Subscription;
	selectedShipTo?: EcallOffShipToDetails;
	unloadingRef?: string;

	constructor(

		readonly dialog: MatDialog,
		readonly router: Router,
		private readonly ecallOffService: ECallOffService,
		private readonly userService: UserService
	) {
		super();
		this.callOffFormGroup = new FormGroup({
			selectedBatchesArray: new FormArray([]),
		});
		this.sub = this.ecallOffService.currentSelectedBatchesStep1.subscribe(data => {
			this.selectedBatches = data;
		});
		this.tempSelectedBatches = [...this.selectedBatches];
		this.sub = this.ecallOffService.currentCopyTo
			.subscribe(copyTo => {
				this.copyTo = copyTo;
			});
		this.sub = this.ecallOffService.currentRddMode
			.subscribe(rddMode => {
				this.rddMode = rddMode;
			});
		this.sub = this.ecallOffService.currentCustCallOffRef
			.subscribe(custCallOffRef => {
				this.custCallOffRef = custCallOffRef;
			});
		this.sub = this.ecallOffService.currentselectedBatchesStep2
			.subscribe(batches => {
				this.calledBatches = batches;
			});
		this.sub = this.ecallOffService.unloadingRef
			.subscribe(unloadinRef => {
				this.unloadingRef = unloadinRef;
			});
		this.sub = this.ecallOffService.selectedShipto
			.subscribe(selectedShipto => {
				this.selectedShipTo = selectedShipto;
			});
		this.sub = this.ecallOffService.currentMills
			.subscribe(currentMills => {
				this.selectedMills = currentMills ?? [];
			});
		this.sub = this.ecallOffService.shipToList
			.subscribe(shipTos => {
				this.shipToDetails = shipTos;
				this.allowShiptoSelection = this.selectedBatches
					.some(selectedBatch => {
						const matchingMill = this.selectedMills.find(mill => mill.code === selectedBatch.mill);
						return matchingMill?.isAllowChangeOfShipto === 1;
					});
			});
		if (this.rddMode || this.custCallOffRef || this.copyTo) {
			this.handleVisibility(this.rddMode);
		} else {
			this.ecallOffService.updateRddMode('ASAP');
		}
		this.user = this.userService.user;
		this.calledBatches = this.calledBatches.length > 0
			? this.calledBatches.filter(calledBatch =>
				this.selectedBatches.some(selectedBatch => calledBatch.id === Number(selectedBatch.id)))
			: [];
		this.selectedBatches = this.selectedBatches.filter(
			selectedBatch => !this.calledBatches.some(
				calledBatch => calledBatch.id === Number(selectedBatch.id)
			)
		);
		for (const selectedBatch of this.selectedBatches) {
			const calledBatch = <CalledBatch>{};
			calledBatch.id = Number(selectedBatch.id);
			calledBatch.millCode = selectedBatch.mill;
			calledBatch.vendorBatchId = selectedBatch.millBatchReference;
			calledBatch.millDescription = selectedBatch.millDescription;
			calledBatch.customerOrderReference = selectedBatch.customerOrderNumber;
			calledBatch.customerArticleReference = selectedBatch.customerArticleReference;
			calledBatch.sapBatchId = selectedBatch.id;
			calledBatch.batchReference = selectedBatch.batchReference;
			calledBatch.grossWeight = selectedBatch.qtyBatchGross;
			calledBatch.netWeight = selectedBatch.qtyBatchNet;
			calledBatch.originalShipTo = selectedBatch.shipTo;
			calledBatch.originalShipToDescription = selectedBatch.shipToDescription;
			calledBatch.supplierOrderReference = selectedBatch.supplierOrderNumber;
			calledBatch.supplierOrderItem = selectedBatch.supplierOrderItemNumber;
			const matchingMill = this.selectedMills.find(mill => mill.code === selectedBatch.mill);
			calledBatch.allowChangeOfShipto = matchingMill?.isAllowChangeOfShipto ?? 0;
			this.calledBatches.push(calledBatch);
		}

		for (const batch of this.calledBatches) {
			const shiptoDetails: ShipToDetails = {
				shipTo: batch.shipTo ?? '',
				shipToDescription: batch.shipToDescription ?? ''
			};
			const batchData = new FormGroup({
				id: new FormControl(batch.id),
				unloadingReference: new FormControl(batch.unloadingReference),
				shipTo: new FormControl(shiptoDetails),
				requestedDate: new FormControl(
					this.rddMode === 'EARLIEST' || this.rddMode === 'BETWEEN'
					|| this.rddMode === 'EXACT' ? batch.requestedDeliveryDateFrom
						: this.rddMode === 'LATEST' ? batch.requestedDeliveryDateTo : null
				),
				fromDate: new FormControl(batch.requestedDeliveryDateFrom),
				toDate: new FormControl(batch.requestedDeliveryDateTo),
				comment: new FormControl(batch.comment),
				millCode: new FormControl(batch.millCode),
				millDescription: new FormControl(batch.millDescription),
				customerOrderReference: new FormControl(batch.customerOrderReference),
				customerArticleReference: new FormControl(batch.customerArticleReference),

			});
			const selectedBatchesArray = <FormArray> this.callOffFormGroup.get('selectedBatchesArray');
			selectedBatchesArray.push(batchData);
		}

		this.callOffFormGroup.get('selectedBatchesArray')?.valueChanges
			.pipe(debounceTime(500))
			.subscribe({
				next: (value: SelectedBatches[]) => {
					for (const batch of this.calledBatches) {
						for (const calledBatch of value) {
							if (batch.id === Number(calledBatch.id)) {
								batch.unloadingReference = calledBatch.unloadingReference;
								batch.shipTo = !this.allowShiptoSelection ? batch.shipTo : calledBatch.shipTo?.shipTo;
								batch.shipToDescription = !this.allowShiptoSelection ? batch.shipToDescription
									: calledBatch.shipTo?.shipToDescription;
								batch.requestedDeliveryDateFrom =
									this.rddMode === 'EARLIEST' || this.rddMode === 'EXACT' ? calledBatch.requestedDate
										: this.rddMode === 'BETWEEN' ? calledBatch.fromDate : '';
								batch.requestedDeliveryDateTo =
									this.rddMode === 'LATEST' || this.rddMode === 'EXACT' ? calledBatch.requestedDate
										: this.rddMode === 'BETWEEN' ? calledBatch.toDate : '';
								batch.millCode = calledBatch.millCode;
								batch.millDescription = calledBatch.millDescription;
								batch.customerOrderReference = calledBatch.customerOrderReference;
								batch.customerArticleReference = calledBatch.customerArticleReference;
							}
						}
					}
					this.ecallOffService.updateSelectedBatchesStep2(this.calledBatches);
				}
			});
		this.ecallOffService.updateSelectedBatchesStep2(this.calledBatches);
	}

	get selectedBatchForm() {
		return (<FormArray> this.callOffFormGroup.controls['selectedBatchesArray']).controls;
	}

	onInfoEntryHeaderChange(form: EcallOffHeaderForm) {
		this.ecallOffService.updateCopyTo(form.copyTo);
		this.ecallOffService.updateCustCallOffRef(form.customerCalloffReference);
		this.ecallOffService.updateRddMode(form.rddMode);
		this.handleVisibility(form.rddMode);
	}

	onInfoEntryHeaderInit(init: EcallOffHeaderInit) {
		this.infoEntryHeaderFormGroup = init.formGroup;
	}

	onViewCard(batch?: CalledBatch) {
		if (!batch?.id) {
			console.warn('Missing batch id to view', { batch });
			return;
		}
		const url = `/order-tracking/batch/card/${ batch.id }`;
		open(url, '_blank');
	}

	checkErrors() {
		this.infoEntryHeaderFormGroup?.markAllAsTouched();
		this.callOffFormGroup.get('selectedBatchesArray')?.markAllAsTouched();
		return this.infoEntryHeaderFormGroup?.valid;
	}

	commaSepEmail = (control: AbstractControl) => {
		const emails = String(control.value).split(';').map(e => e.trim());
		const forbidden = emails.some(email =>
			Validators.email(new FormControl(email)));
		return forbidden ? { toAddress: { value: <string> control.value } } : null;
	};

	openDialog(value: string | undefined, index: number) {
		const dialogRef = this.dialog.open(DialogSaveCommentComponent, {
			width: '60%',
			data: {
				value: value ?? ''
			}
		});
		this.dialogSubscription = dialogRef.afterClosed().subscribe((result?: string) => {
			if (index >= 0 && index < this.calledBatches.length) {
				this.calledBatches[index].comment = result;
			}
		});
	}

	onInfoEntryCalloffHeaderApplyAll(form: InfoEntryCalloffHeaderForm) {
		if (Object.keys(form.shipTo).length !== 0) {
			this.ecallOffService.updateSelectedShipto(form.shipTo);
		}
		this.ecallOffService.updateUnloadingRef(form.unloadingReference);
		for (let i = 0; i < this.calledBatches.length; i++) {
			const rows = <FormArray> this.callOffFormGroup.get('selectedBatchesArray');
			const shipToFromRow = (<{ shipTo: ShipToDetails }>rows.controls[i].value).shipTo;
			const shipToFromForm = form.shipTo;
			const shipToFromCalledBatch: ShipToDetails = {
				shipTo: this.calledBatches[i].shipTo ?? '',
				shipToDescription: this.calledBatches[i].shipToDescription ?? ''
			};
			let shipToToSave: ShipToDetails;
			// if header shipto has a value and for the current batch mill change of shipto is allowed
			// , then apply that value
			if (Object.keys(form.shipTo).length !== 0 && this.calledBatches[i].allowChangeOfShipto === 1) {
				shipToToSave = shipToFromForm;
			// if there is already value in the sub shipto rows, keep it
			} else if (Object.keys(shipToFromRow).length !== 0) {
				shipToToSave = shipToFromRow;
			// if no value from form and nothing is already selected keep the value of batch
			} else {
				shipToToSave = shipToFromCalledBatch;
			}

			rows.controls[i].patchValue({
				unloadingReference: form.unloadingReference,
				requestedDate: form.requestedDate,
				fromDate: form.fromDate,
				toDate: form.toDate,
				shipTo: shipToToSave
			});
			this.calledBatches[i].unloadingReference = form.unloadingReference;
			this.calledBatches[i].shipTo = shipToToSave.shipTo;
			this.calledBatches[i].shipToDescription = shipToToSave.shipToDescription;
			this.calledBatches[i].requestedDeliveryDateFrom =
				this.rddMode === 'EARLIEST' || this.rddMode === 'EXACT' ? form.requestedDate?.toString()
					: this.rddMode === 'BETWEEN' ? form.fromDate?.toString() : undefined;
			this.calledBatches[i].requestedDeliveryDateTo =
				this.rddMode === 'LATEST' || this.rddMode === 'EXACT' ? form.requestedDate?.toString()
					: this.rddMode === 'BETWEEN' ? form.toDate?.toString() : undefined;
			this.ecallOffService.updateSelectedBatchesStep2(this.calledBatches);
		}
	}

	deleteBatch(index: number) {
		this.tempSelectedBatches.splice(index, 1);
		this.calledBatches.splice(index, 1);
		this.ecallOffService.updateSelectedBatchesStep2(this.calledBatches);
		this.ecallOffService.setSelectedBatches(this.tempSelectedBatches);
		const rows = <FormArray> this.callOffFormGroup.get('selectedBatchesArray');
		rows.removeAt(index);
	}

	handleVisibility(value?: string) {
		switch (value) {
		case 'EARLIEST':
		case 'EXACT':
		case 'LATEST':
			this.reqDate = true;
			this.fromTo = false;
			break;
		case 'BETWEEN':
			this.reqDate = false;
			this.fromTo = true;
			break;
		default:
			this.reqDate = false;
			this.fromTo = false;
			break;
		}
	}

	getTonnage() {
		let sum = 0;
		this.ecallOffService.updateSelectedBatchesStep2(this.calledBatches);
		for (const batch of this.calledBatches) {
			sum += this.weightDisplay === 'net'
				? Number(batch.netWeight)
				: Number(batch.grossWeight);
		}
		return sum;
	}

	getWeight(calledBatch: CalledBatch) {
		return this.weightDisplay === 'net' ? calledBatch.netWeight : calledBatch.grossWeight;
	}

	ngOnDestroy() {
		if (this.orderSub !== undefined) {
			this.orderSub.unsubscribe();
		}
		this.dialogSubscription.unsubscribe();
	}

	dateFilter = (d: Date | null): boolean => this.ecallOffService.dateFilter(d);

	isWeekend(date?: Date | null): boolean {
		if (!date) {
			return false;
		}
		const day = new Date(date).getDay();
		return day === 0 || day === 6;
	}

	getMinToDate(date?: Date) {
		return this.ecallOffService.getMinToDate(date);
	}

	getMaxFromDate(date?: Date) {
		return this.ecallOffService.getMaxFromDate(date);
	}

	compareShipTo(shipTo1: ShipToDetails, shipTo2: string | ShipToDetails): boolean {
		if (typeof shipTo2 === 'object' && 'shipTo' in shipTo2) {
			return shipTo1.shipTo === shipTo2.shipTo;
		}

		if (typeof shipTo2 === 'string') {
			const normalizedShipTo2 = shipTo2.replace(/^0+/, '');
			return shipTo1.shipTo === normalizedShipTo2;
		}

		return false;
	}

	onShipToChange(selectedValue: ShipToDetails, index: number): void {
		this.calledBatches[index].shipTo = selectedValue.shipTo;
		this.calledBatches[index].shipToDescription = selectedValue.shipToDescription;
	}
}
